import { useState, useRef, useEffect, useCallback } from 'react';
import {
  useTheme,
  resolveLessonThemeColor,
  DEFAULT_LESSON_THEME_COLOR,
  type LessonThemeColor,
} from '@course-authoring/shared-types';

interface LessonTitleSectionProps {
  title: string;
  themeColor: LessonThemeColor | undefined;
  onThemeColorChange: (color: LessonThemeColor) => void;
}

const THEME_COLOR_OPTIONS: LessonThemeColor[] = ['color1', 'color2', 'color3', 'color4'];

export function LessonTitleSection({
  title,
  themeColor,
  onThemeColorChange,
}: LessonTitleSectionProps) {
  const { tokens } = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const resolvedColor = themeColor ?? DEFAULT_LESSON_THEME_COLOR;
  const lineColor = resolveLessonThemeColor(resolvedColor, tokens.lessonColors);

  const handleTogglePicker = useCallback(() => {
    setPickerOpen((prev) => !prev);
  }, []);

  const handleSelectColor = useCallback(
    (color: LessonThemeColor) => {
      onThemeColorChange(color);
      setPickerOpen(false);
    },
    [onThemeColorChange],
  );

  useEffect(() => {
    if (!pickerOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pickerOpen]);

  const styles = createStyles(tokens, lineColor);

  return (
    <div style={styles.container}>
      {/* 10-column grid: 1 left gutter | 6 content | 3 right gutter */}
      <div style={styles.grid}>
        <div style={styles.content}>
          <div style={styles.label}>LESSON TITLE</div>
          <div style={styles.title}>
            {title || 'Untitled Lesson'}
          </div>
          <div style={styles.lineRow}>
            <div style={styles.themeColorLine} />
            <div style={styles.pickerWrapper} ref={pickerRef}>
              <button
                type="button"
                onClick={handleTogglePicker}
                style={styles.pickerButton}
                title="Lesson Theme Color"
              >
                Lesson Theme Color
                <span style={styles.pickerArrow}>{pickerOpen ? '\u25B2' : '\u25BC'}</span>
              </button>
              {pickerOpen && (
                <div style={styles.pickerDropdown}>
                  {THEME_COLOR_OPTIONS.map((color, i) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => handleSelectColor(color)}
                      style={{
                        ...styles.colorSwatch,
                        backgroundColor: resolveLessonThemeColor(color, tokens.lessonColors),
                        ...(color === resolvedColor ? styles.colorSwatchActive : {}),
                      }}
                      title={`Theme color ${i + 1}`}
                      aria-label={`Select theme color ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function createStyles(
  tokens: ReturnType<typeof useTheme>['tokens'],
  lineColor: string,
) {
  return {
    container: {
      width: '100%',
      padding: `${tokens.spacing["2xl"]} 0 ${tokens.spacing.lg}`,
      boxSizing: 'border-box' as const,
      border: `2px dashed ${tokens.borders.default}`,
      borderRadius: tokens.radius.lg,
      backgroundColor: tokens.backgrounds.app,
      boxShadow: tokens.shadow.sm,
    },
    // 10-column grid: 1 left gutter | 6 content | 3 right gutter
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr 6fr 3fr',
      width: '100%',
    },
    content: {
      gridColumn: '2 / 3',
      display: 'flex',
      flexDirection: 'column' as const,
    },
    label: {
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '1px',
      color: tokens.text.subtle,
      fontFamily: tokens.typography.fontFamily,
      marginBottom: tokens.spacing.sm,
      textTransform: 'uppercase' as const,
    },
    title: {
      fontSize: '60px',
      fontWeight: 700,
      lineHeight: '65px',
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      wordBreak: 'break-word' as const,
      marginBottom: '30px',
    },
    lineRow: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.md,
    },
    themeColorLine: {
      height: '9px',
      width: '262px',
      backgroundColor: lineColor,
      transition: 'background-color 200ms ease',
    },
    pickerWrapper: {
      position: 'relative' as const,
    },
    pickerButton: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.xs,
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      fontSize: '13px',
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.muted,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      whiteSpace: 'nowrap' as const,
    },
    pickerArrow: {
      fontSize: '8px',
      marginLeft: '2px',
    },
    pickerDropdown: {
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      marginTop: tokens.spacing.xs,
      display: 'flex',
      gap: tokens.spacing.sm,
      padding: tokens.spacing.sm,
      backgroundColor: tokens.backgrounds.app,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      boxShadow: tokens.shadow.md,
      zIndex: 10,
    },
    colorSwatch: {
      width: '32px',
      height: '32px',
      borderRadius: '6px',
      border: '3px solid transparent',
      cursor: 'pointer',
      padding: 0,
      transition: 'border-color 150ms ease',
    },
    colorSwatchActive: {
      border: `3px solid ${tokens.text.primary}`,
    },
  };
}
